import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Layers, CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { optimizeSupabaseImage, IMAGE_PRESETS } from '@/lib/utils/imageOptimization';

interface Program {
  id: string;
  name: string;
  description?: string | null;
  cover_image?: string | null;
  is_active?: boolean | null;
}

interface ProgramCardProps {
  program: Program;
  isActive: boolean;
  activityCount?: number;
  onClick: () => void;
  isLoading?: boolean;
}

const defaultGradients = [
  'from-blue-600 to-indigo-700',
  'from-sky-600 to-cyan-700',
  'from-purple-600 to-violet-700',
  'from-orange-600 to-red-700',
  'from-pink-600 to-rose-700',
];

export function ProgramCard({ program, isActive, activityCount = 0, onClick, isLoading }: ProgramCardProps) {
  const { t } = useTranslation();
  
  // Get a consistent gradient based on program id
  const gradientIndex = program.id.charCodeAt(0) % defaultGradients.length;
  const fallbackGradient = defaultGradients[gradientIndex];

  return (
    <Card 
      className={`
        relative overflow-hidden cursor-pointer transition-all duration-300 
        group border-2
        ${isActive 
          ? 'border-primary shadow-lg shadow-primary/20' 
          : 'border-transparent hover:border-primary/40 hover:shadow-md'
        }
        ${isLoading ? 'pointer-events-none' : ''}
      `}
      onClick={onClick}
    >
      {/* Background */}
      <div className="absolute inset-0">
        {program.cover_image ? (
          <img
            src={optimizeSupabaseImage(program.cover_image, IMAGE_PRESETS.card)}
            alt={program.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${fallbackGradient} transition-all duration-500 group-hover:opacity-90`} />
        )}
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 transition-opacity duration-300 group-hover:from-black/70" />
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 z-20 bg-black/40 backdrop-blur-sm flex items-center justify-center animate-fade-in">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        </div>
      )}

      <CardContent className="relative z-10 p-4 h-full flex flex-col justify-end min-h-[140px] sm:min-h-[160px]">
        {/* Active Badge */}
        {isActive && (
          <Badge className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-primary text-primary-foreground text-[10px] sm:text-xs px-2 py-0.5">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            {t('common:program.active', 'Active')}
          </Badge>
        )}

        {/* Program Info */}
        <div className="mt-auto">
          <h4 className="font-bold text-white text-sm sm:text-lg leading-tight mb-1 line-clamp-2 transition-transform duration-300 group-hover:translate-x-1">
            {program.name}
          </h4>
          {program.description && (
            <p className="text-white/70 text-[10px] sm:text-xs line-clamp-2 mb-2 transition-opacity duration-300 group-hover:text-white/90">
              {program.description}
            </p>
          )}
          <div className="flex items-center gap-1 text-white/60 text-[10px] sm:text-xs transition-colors duration-300 group-hover:text-white/80">
            <Layers className="w-3 h-3" />
            <span>{activityCount} {t('common:program.activities', 'activities')}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
